/**
 * TwinCalibrationView (P180.4) -- validate the twin against reality.
 *
 * Shows the modelled recovery (service RTO / IBS slowest-dependency RTO)
 * vs the actuals observed in closed incidents + completed drills, with a
 * per-entity suggestion. The calibration loop is what lets us claim
 * "digital twin": a model scored against the bank's lived experience.
 * Admin-only; hardcoded English to match the sibling admin consoles.
 */
import { useTranslation } from "react-i18next";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { useMe } from "shared/api/useMe";
import type { TwinCalibrationRow } from "shared/api/types";
import {
  type CalibrationSource,
  useApplyCalibration,
  useApplyIbsCalibration,
  useTwinCalibration,
} from "shared/admin/useTwinCalibration";
import { useToast } from "shared/toast/ToastProvider";

const SUGGESTION: Record<string, { cls: string; label: string }> = {
  raise: { cls: "bg-rag-redBg text-rag-red", label: "raise model" },
  lower: { cls: "bg-rag-amberBg text-rag-amber", label: "lower model" },
  ok: { cls: "bg-rag-greenBg text-rag-green", label: "ok" },
  no_model: { cls: "bg-slate-100 text-slate-500", label: "no model" },
  insufficient_data: { cls: "bg-slate-100 text-slate-400", label: "need data" },
};

function SuggestionPill({ s }: { s: string }) {
  const m = SUGGESTION[s] ?? SUGGESTION.insufficient_data!;
  return (
    <span
      data-testid="calib-suggestion"
      data-suggestion={s}
      className={`rounded px-2 py-0.5 text-[11px] font-medium ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

function hrs(n: number | null): string {
  return n == null ? "--" : `${n}h`;
}

function CalibrationTable({
  caption,
  modelledLabel,
  rows,
  onApply,
  pendingId,
  onApplyIbs,
  pendingIbsId,
}: {
  caption: string;
  modelledLabel: string;
  rows: TwinCalibrationRow[];
  onApply?: (id: string) => void;
  pendingId?: string | null;
  // P228: IBS-override apply -- shows a suggested-factor column + an
  // "Apply Nx" button that writes recovery_factor_override.
  onApplyIbs?: (id: string) => void;
  pendingIbsId?: string | null;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <p data-testid="calib-empty" className="text-sm text-slate-500">
        {caption}
        {t("screens.twinCalibrationView.noObservedHistoryYetNeedClosedIncidents")}
      </p>
    );
  }
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="mb-2 text-sm font-semibold text-slate-900">{caption}</h2>
      <table className="w-full text-start text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="py-1">{t("screens.twinCalibrationView.entity")}</th>
            <th className="py-1">{modelledLabel}</th>
            <th className="py-1">{t("screens.twinCalibrationView.observedMean")}</th>
            <th className="py-1">{t("screens.twinCalibrationView.samples")}</th>
            <th className="py-1">{t("screens.twinCalibrationView.error")}</th>
            <th className="py-1">{t("screens.twinCalibrationView.suggestion")}</th>
            {onApplyIbs && (
              <th className="py-1">{t("screens.twinCalibrationView.override")}</th>
            )}
            {(onApply || onApplyIbs) && <th className="py-1" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const applicable = r.suggestion === "raise" || r.suggestion === "lower";
            const ibsApplicable = onApplyIbs != null && r.suggested_override != null;
            return (
              <tr
                key={r.entity_id}
                data-testid={`calib-row-${r.entity_id}`}
                className="border-t border-slate-100"
              >
                <td className="py-1.5 text-slate-800">{r.entity_name}</td>
                <td className="py-1.5 tabular-nums text-slate-600">
                  {hrs(r.modelled_hours)}
                </td>
                <td className="py-1.5 tabular-nums text-slate-600">
                  {hrs(r.observed_mean_hours)}
                </td>
                <td className="py-1.5 tabular-nums text-slate-500">{r.sample_count}</td>
                <td className="py-1.5 tabular-nums text-slate-700">
                  {r.error_pct == null
                    ? "--"
                    : `${r.error_pct > 0 ? "+" : ""}${r.error_pct}%`}
                </td>
                <td className="py-1.5">
                  <SuggestionPill s={r.suggestion} />
                </td>
                {onApplyIbs && (
                  <td
                    data-testid={`calib-override-${r.entity_id}`}
                    className="py-1.5 tabular-nums text-slate-600"
                  >
                    {r.suggested_override == null ? "--" : `${r.suggested_override}x`}
                  </td>
                )}
                {onApply && (
                  <td className="py-1.5 text-end">
                    {applicable && (
                      <button
                        type="button"
                        data-testid={`calib-apply-${r.entity_id}`}
                        disabled={pendingId === r.entity_id}
                        onClick={() => onApply(r.entity_id)}
                        className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
                      >
                        {pendingId === r.entity_id
                          ? t("screens.twinCalibrationView.applying")
                          : `Apply ${r.observed_mean_hours}h`}
                      </button>
                    )}
                  </td>
                )}
                {onApplyIbs && (
                  <td className="py-1.5 text-end">
                    {ibsApplicable && (
                      <button
                        type="button"
                        data-testid={`calib-apply-ibs-${r.entity_id}`}
                        disabled={pendingIbsId === r.entity_id}
                        onClick={() => onApplyIbs(r.entity_id)}
                        className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
                      >
                        {pendingIbsId === r.entity_id
                          ? t("screens.twinCalibrationView.applying")
                          : `Apply ${r.suggested_override}x`}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

export function TwinCalibrationView(): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const calib = useTwinCalibration();
  const apply = useApplyCalibration();
  const applyIbs = useApplyIbsCalibration();
  const toast = useToast();

  const onApply = (serviceId: string, source: CalibrationSource) => {
    apply.mutate(
      { serviceId, source },
      {
        onSuccess: (res) => {
          const noun =
            res.source === "telemetry" ? "telemetry episode(s)" : "incident(s)";
          toast.push({
            tone: res.changed ? "success" : "info",
            message: res.changed
              ? `${res.service_name}: RTO ${res.old_rto_hours ?? "--"}h -> ${res.new_rto_hours}h`
              : `${res.service_name}: already at ${res.new_rto_hours}h`,
            detail: `from ${res.sample_count} observed ${noun}`,
          });
        },
        onError: (err) =>
          toast.push({ tone: "error", message: "Apply failed", detail: err.message }),
      },
    );
  };
  const pendingFor = (source: CalibrationSource): string | null =>
    apply.isPending && apply.variables?.source === source
      ? apply.variables.serviceId
      : null;

  const onApplyIbs = (ibsId: string) => {
    applyIbs.mutate(ibsId, {
      onSuccess: (res) =>
        toast.push({
          tone: res.changed ? "success" : "info",
          message: res.changed
            ? `${res.ibs_name}: recovery override ${res.old_override ?? "--"} -> ${res.new_override}x`
            : `${res.ibs_name}: already at ${res.new_override}x`,
          detail: `from ${res.sample_count} drill(s) (observed ${res.observed_mean_hours}h vs ${res.binding_rto_hours}h binding)`,
        }),
      onError: (err) =>
        toast.push({ tone: "error", message: "Apply failed", detail: err.message }),
    });
  };

  if (me.data && !me.data.is_admin) {
    return <AdminOnlyPanel surface="Twin calibration" />;
  }

  return (
    <section data-testid="twin-calibration-view" className="flex flex-col gap-4">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
          {t("screens.twinCalibrationView.adminTwinCalibration")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="drill"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.twinCalibrationView.twinCalibration")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t("screens.twinCalibrationView.howCloseTheModelRunsToReality")}
        </p>
      </header>

      {calib.isError && (
        <p data-testid="calib-error" className="text-sm text-rose-700">
          {t("screens.twinCalibrationView.failedToLoadTheCalibrationReport")}
        </p>
      )}
      {calib.isLoading && (
        <p data-testid="calib-loading" className="text-sm text-slate-500">
          {t("screens.twinCalibrationView.scoringTheModelAgainstHistory")}
        </p>
      )}

      {calib.data && (
        <>
          <div data-testid="calib-headline" className="flex flex-wrap gap-2 text-xs">
            <span className="rounded bg-slate-100 px-2 py-1 text-slate-700">
              {calib.data.services_sampled} {t("screens.twinCalibrationView.services")}{" "}
              {calib.data.ibses_sampled} {t("screens.twinCalibrationView.ibses")}{" "}
              {calib.data.telemetry_sampled}{" "}
              {t("screens.twinCalibrationView.telemetrySampled")}
            </span>
            <span
              className={`rounded px-2 py-1 ${
                calib.data.flagged_count > 0
                  ? "bg-rag-amberBg text-rag-amber"
                  : "bg-rag-greenBg text-rag-green"
              }`}
            >
              {calib.data.flagged_count}{" "}
              {t("screens.twinCalibrationView.flaggedForAdjustment")}
            </span>
            {calib.data.mean_abs_error_pct != null && (
              <span className="rounded bg-slate-100 px-2 py-1 text-slate-700">
                {t("screens.twinCalibrationView.meanAbsError")}{" "}
                {calib.data.mean_abs_error_pct}%
              </span>
            )}
          </div>
          <CalibrationTable
            caption={t("screens.twinCalibrationView.serviceRtoVsIncidentActuals")}
            modelledLabel="Modelled RTO"
            rows={calib.data.service_rows}
            onApply={(id) => onApply(id, "incidents")}
            pendingId={pendingFor("incidents")}
          />
          <CalibrationTable
            caption={t("screens.twinCalibrationView.serviceRtoVsLiveTelemetry")}
            modelledLabel="Modelled RTO"
            rows={calib.data.telemetry_rows}
            onApply={(id) => onApply(id, "telemetry")}
            pendingId={pendingFor("telemetry")}
          />
          <CalibrationTable
            caption={t("screens.twinCalibrationView.ibsRecoveryVsDrillActuals")}
            modelledLabel="Modelled recovery"
            rows={calib.data.ibs_rows}
            onApplyIbs={onApplyIbs}
            pendingIbsId={applyIbs.isPending ? applyIbs.variables : null}
          />
        </>
      )}
    </section>
  );
}
